import { useRef, useEffect, useMemo, useCallback, useState } from "react";
import { CaseBriefChatMessage } from "./CaseBriefChatMessage";
import { ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { ChevronDown, Upload, Globe, File as FileIcon, Link as LinkIcon, X } from "lucide-react";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { useTranslation } from "react-i18next";
import { InnoveraIcon } from "@libs/zapi-ui/assets";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TPreUploadedDocument } from "@/zapi-frontend/pages/CreateProjectPage/model/usePreUploadDocuments";
import type { TBriefStatus } from "./ProjectCreationHeader";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import { removeTimestampPrefix } from "@/zapi-frontend/shared/lib/splitFileName";

type ChatStep = "language" | "decision" | "files" | "dimensions" | "setupComplete" | "chat";

export interface CreateProjectChatMessageListProps {
  messages: ChatMessageType[];
  isLoading: boolean;
  loadingMessageId: string | null;
  step: ChatStep;
  onLanguageSelect: (language: string) => void;
  onAddFiles: () => void;
  onAddWebLink: () => void;
  onSkipFiles: () => void;
  onSaveFiles: () => void;
  onRemoveFile: (documentId: number) => void;
  uploadedDocuments: TPreUploadedDocument[];
  isUploading: boolean;
  statusMessage?: string | null;
  languageOptions?: { label: string; value: string }[];
  isLanguagesLoading?: boolean;
  labels?: {
    upload: string;
    webLink: string;
    skip: string;
    save: string;
    launchAnalysis: string;
    keepEditing: string;
    dimensionConfirm: string;
    dimensionSkip: string;
    nextStep: string;
    confirmDecision?: string;
    otherDecision?: string;
  };
  // Under a decision suggested from the documents: accept it, or type your own.
  onConfirmDecision?: () => void;
  onOtherDecision?: () => void;
  // A check's own tappable answer, sent as the user's reply.
  onQuickReply?: (answer: string) => void;
  onNextStep?: () => void;
  essentialsDismissed?: boolean;
  onLaunchAnalysis?: () => void;
  onKeepEditing?: () => void;
  launchBriefStatus?: TBriefStatus;
  isLaunching?: boolean;
  dimensions?: TOrganizationProjectDimension[];
  pendingDimensionIndex?: number;
  selectedDimensionOption?: string;
  onDimensionOptionChange?: (value: string) => void;
  onDimensionConfirm?: (value: string) => void;
  onDimensionSkip?: () => void;
  onSkipVersionName?: () => void;
  readOnly?: boolean;
}

const isEssentialsCoveredMessage = (message: ChatMessageType) => {
  if (message.role !== "assistant") return false;
  try {
    const parsed = JSON.parse(message.content);
    return parsed?.type === "essentials_covered";
  } catch {
    return false;
  }
};

const isVersionNamePromptMessage = (message: ChatMessageType) => {
  if (message.role !== "assistant") return false;
  try {
    const parsed = JSON.parse(message.content);
    return parsed?.type === "version_name_prompt";
  } catch {
    return false;
  }
};

// Walkthrough messages the user can answer with "Yes, that's right" or their own words.
const CONFIRMABLE_WALKTHROUGH_KEYS = new Set([
  "walkthroughFormat",
  "walkthroughScope",
  "walkthroughIntent",
  "walkthroughCheck",
  "walkthroughReady"
]);

const getTemplateOptions = (message: ChatMessageType): string[] | null => {
  try {
    const options = JSON.parse(message.content)?.templateParams?.options;
    return Array.isArray(options) ? options.filter((o): o is string => typeof o === "string") : null;
  } catch {
    return null;
  }
};

const getTemplateKey = (message: ChatMessageType): string | null => {
  try {
    return JSON.parse(message.content)?.templateKey ?? null;
  } catch {
    return null;
  }
};

const WALKTHROUGH_CLOSING_KEYS = new Set(["walkthroughDone", "walkthroughDoneWithGaps"]);

const hashMessages = (messages: ChatMessageType[]) => {
  return messages.map((msg) => msg.id).join("-");
};

type TActionButtonProps = {
  filled: boolean;
  icon?: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
};

function ActionButton({
  filled,
  icon,
  onClick,
  children,
  disabled,
  className,
  "data-testid": dataTestId
}: TActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      className={cn(
        "flex gap-1 h-6 shrink-0 items-center whitespace-nowrap px-2 py-[5px] rounded-md transition-colors text-[12px] leading-[11px] font-medium disabled:opacity-50 disabled:pointer-events-none",
        className,
        filled
          ? "bg-innoveraZinc-900 text-white hover:bg-innoveraZinc-800"
          : "border border-innoveraZinc-150 text-innoveraZinc-900 hover:bg-innoveraZinc-50"
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function LaunchAnalysisButton({
  dark,
  disabled,
  isLaunching,
  onClick,
  children,
  "data-testid": dataTestId
}: {
  dark: boolean;
  disabled: boolean;
  isLaunching: boolean;
  onClick: () => void;
  children: React.ReactNode;
  "data-testid": string;
}) {
  const { t } = useTranslation();
  const iconFillClass = isLaunching
    ? undefined
    : disabled
      ? "[&_svg>path]:fill-innoveraZinc-400"
      : dark
        ? "[&_svg>path]:fill-white"
        : "[&_svg>path]:fill-innoveraZinc-900";
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-testid={dataTestId}
      className={cn(
        "flex gap-1 h-6 shrink-0 items-center whitespace-nowrap px-2 py-[5px] rounded-md transition-colors text-[12px] leading-[11px] font-medium",
        "disabled:opacity-50 disabled:pointer-events-none",
        iconFillClass,
        dark
          ? "bg-innoveraZinc-900 text-white hover:bg-innoveraZinc-800"
          : "bg-innoveraZinc-100 text-innoveraZinc-900 hover:bg-innoveraZinc-150"
      )}
    >
      {isLaunching ? <Spinner aria-label={t("loading")} className="size-3" /> : <InnoveraIcon className="size-3" />}
      {children}
    </button>
  );
}

export function CreateProjectChatMessageList({
  messages,
  isLoading,
  loadingMessageId,
  step,
  onLanguageSelect,
  onAddFiles,
  onAddWebLink,
  onSkipFiles,
  onSaveFiles,
  onRemoveFile,
  uploadedDocuments,
  isUploading,
  statusMessage,
  languageOptions = [
    { label: "English", value: "english" },
    { label: "日本語", value: "japanese" }
  ],
  isLanguagesLoading = false,
  labels = {
    upload: "Upload",
    webLink: "Web Link",
    skip: "Skip",
    save: "Save",
    launchAnalysis: "Launch Analysis",
    keepEditing: "Keep Editing",
    dimensionConfirm: "Confirm",
    dimensionSkip: "Skip",
    nextStep: "Next Step"
  },
  onNextStep,
  essentialsDismissed = false,
  onLaunchAnalysis,
  onKeepEditing,
  launchBriefStatus = "incomplete",
  isLaunching = false,
  dimensions = [],
  pendingDimensionIndex = 0,
  selectedDimensionOption = "",
  onDimensionOptionChange,
  onDimensionConfirm,
  onDimensionSkip,
  onSkipVersionName,
  onConfirmDecision,
  onOtherDecision,
  onQuickReply,
  readOnly = false
}: CreateProjectChatMessageListProps) {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  // The question whose "Other" the user picked: it stays highlighted until the typed answer is sent.
  const [otherPickedFor, setOtherPickedFor] = useState<string | null>(null);

  const messagesHash = useMemo(() => hashMessages(messages), [messages]);

  const checkIfScrollable = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    setShowScrollDown(!isNearBottom && container.scrollHeight > container.clientHeight);
  }, []);

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight });
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const observer = new ResizeObserver(checkIfScrollable);
    observer.observe(content);
    return () => observer.disconnect();
  }, [checkIfScrollable]);

  useEffect(() => {
    const timeout = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeout);
  }, [messagesHash, loadingMessageId, scrollToBottom, uploadedDocuments.length]);

  const lastAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return i;
    }
    return -1;
  }, [messages]);

  const renderFilesList = () => {
    if (uploadedDocuments.length === 0) return null;
    return (
      <div className="flex flex-col gap-1.5 w-full">
        {uploadedDocuments.map((doc) => {
          const isWebLink = !!doc.webUrl;
          return (
            <div key={doc.documentId} className="flex gap-2 items-center w-full">
              <div className="flex flex-1 gap-2 items-center min-w-0">
                {isWebLink ? (
                  <LinkIcon className="size-4 shrink-0 text-innoveraZinc-900" />
                ) : (
                  <FileIcon className="size-4 shrink-0 text-innoveraZinc-900" />
                )}
                <FigmaTypography variant="body-2" color="zinc900" className="truncate">
                  {isWebLink ? doc.webUrl : removeTimestampPrefix(doc.fileName)}
                </FigmaTypography>
              </div>
              <button
                type="button"
                onClick={() => onRemoveFile(doc.documentId)}
                aria-label="Remove"
                data-testid={`create-project-chat-remove-file-${doc.documentId}-button`}
                className="size-4 shrink-0 flex items-center justify-center text-innoveraZinc-500 hover:text-innoveraZinc-900"
              >
                <X className="size-4" />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderStepButtons = (afterIndex: number) => {
    if (readOnly) return null;
    if (afterIndex !== lastAssistantIndex) return null;

    const lastMessage = messages[afterIndex];

    if (lastMessage && isVersionNamePromptMessage(lastMessage) && onSkipVersionName) {
      return (
        <div className="ml-[58px] pr-6 pb-3 flex gap-2 items-start">
          <ActionButton
            filled={false}
            onClick={onSkipVersionName}
            data-testid="create-project-chat-skip-version-name-button"
          >
            {labels.skip}
          </ActionButton>
        </div>
      );
    }

    if (lastMessage && isEssentialsCoveredMessage(lastMessage) && !essentialsDismissed) {
      return (
        <div className="ml-[58px] pr-6 pb-3 flex flex-wrap gap-2 items-start">
          <LaunchAnalysisButton
            dark={launchBriefStatus === "complete"}
            disabled={launchBriefStatus !== "complete" || isLaunching}
            isLaunching={isLaunching}
            onClick={() => onLaunchAnalysis?.()}
            data-testid="create-project-chat-launch-analysis-button"
          >
            {labels.launchAnalysis}
          </LaunchAnalysisButton>
          <ActionButton
            filled={false}
            onClick={() => onKeepEditing?.()}
            data-testid="create-project-chat-keep-editing-button"
          >
            {labels.keepEditing}
          </ActionButton>
        </div>
      );
    }

    // The walkthrough's closing message offers the launch it mentions.
    if (step === "chat" && lastMessage && WALKTHROUGH_CLOSING_KEYS.has(getTemplateKey(lastMessage) ?? "") && onLaunchAnalysis) {
      return (
        <div className="ml-[58px] pr-6 pb-3 flex gap-2 items-start">
          <LaunchAnalysisButton
            dark={launchBriefStatus === "complete"}
            disabled={launchBriefStatus === "empty" || isLaunching}
            isLaunching={isLaunching}
            onClick={onLaunchAnalysis}
            data-testid="create-project-chat-walkthrough-launch-button"
          >
            {labels.launchAnalysis}
          </LaunchAnalysisButton>
        </div>
      );
    }

    // After the "no changes it is" back confirmation, offer a Next Step button to continue.
    if (lastMessage && getTemplateKey(lastMessage) === "backAtProjectSetupNoChanges" && onNextStep) {
      return (
        <div className="ml-[58px] pr-6 pb-3 flex gap-2 items-start">
          <ActionButton filled onClick={onNextStep} data-testid="create-project-chat-next-step-button">
            {labels.nextStep}
          </ActionButton>
        </div>
      );
    }

    // A walkthrough question already answered (the user's reply follows it) keeps no buttons.
    const answered = afterIndex !== messages.length - 1;
    const confirmableKey = lastMessage && !answered ? getTemplateKey(lastMessage) : null;
    const keyQuestionAnswers =
      step === "chat" && confirmableKey === "walkthroughQuestion" && lastMessage ? getTemplateOptions(lastMessage) : null;
    if (
      onConfirmDecision &&
      onOtherDecision &&
      ((step === "decision" && confirmableKey === "decisionFromDocuments") ||
        (step === "chat" && !!confirmableKey && CONFIRMABLE_WALKTHROUGH_KEYS.has(confirmableKey)) ||
        !!keyQuestionAnswers?.length)
    ) {
      // Checks, and a key question that offers choices, carry their own answers; the "is this right?" questions get "Yes, that's right".
      const ownAnswers =
        (confirmableKey === "walkthroughCheck" || confirmableKey === "walkthroughReady") && lastMessage
          ? getTemplateOptions(lastMessage) ?? []
          : keyQuestionAnswers;
      const otherPicked = !!lastMessage && otherPickedFor === lastMessage.id;
      const unpicked = otherPicked ? "opacity-50" : undefined;
      return (
        <div className="ml-[58px] pr-6 pb-3 flex flex-wrap gap-2 items-start">
          {ownAnswers ? (
            ownAnswers.map((answer, index) => (
              <ActionButton
                key={answer}
                filled={!otherPicked && index === 0}
                className={unpicked}
                onClick={() => onQuickReply?.(answer)}
                data-testid="create-project-chat-check-answer-button"
              >
                {answer}
              </ActionButton>
            ))
          ) : (
            <ActionButton
              filled={!otherPicked}
              className={unpicked}
              onClick={onConfirmDecision}
              data-testid="create-project-chat-confirm-decision-button"
            >
              {labels.confirmDecision}
            </ActionButton>
          )}
          {confirmableKey !== "walkthroughReady" && (
            <ActionButton
              filled={otherPicked}
              onClick={() => {
                if (lastMessage) setOtherPickedFor(lastMessage.id);
                onOtherDecision?.();
              }}
              data-testid="create-project-chat-other-decision-button"
            >
              {labels.otherDecision}
            </ActionButton>
          )}
        </div>
      );
    }

    if (step === "setupComplete" && onNextStep) {
      return (
        <div className="ml-[58px] pr-6 pb-3 flex gap-2 items-start">
          <ActionButton filled onClick={onNextStep} data-testid="create-project-chat-next-step-button">
            {labels.nextStep}
          </ActionButton>
        </div>
      );
    }

    if (step === "language") {
      if (isLanguagesLoading) {
        return (
          <div className="ml-[58px] flex items-center gap-2 py-2 text-[14px] text-innoveraZinc-500">
            <Spinner aria-label={t("loading")} className="size-4 shrink-0" />
          </div>
        );
      }
      return (
        <div className="ml-[58px] mt-2 flex flex-col items-start gap-2 pb-3">
          {languageOptions.map((lang) => (
            <button
              key={lang.value}
              type="button"
              onClick={() => onLanguageSelect(lang.value)}
              data-testid={`create-project-chat-language-${lang.value}-button`}
              className="h-6 rounded-md border border-innoveraZinc-150 px-2 py-[5px] text-left text-[12px] font-medium leading-[11px] text-innoveraZinc-900 transition-colors hover:bg-innoveraZinc-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-innoveraZinc-200"
            >
              {lang.label}
            </button>
          ))}
        </div>
      );
    }

    if (step === "files") {
      const hasItems = uploadedDocuments.length > 0;
      return (
        <div className="ml-[58px] flex flex-col gap-2.5 pb-3 pr-6">
          {renderFilesList()}
          <div className="flex flex-wrap items-start gap-2">
            <ActionButton
              filled={!hasItems}
              icon={<Upload className="size-3" />}
              onClick={onAddFiles}
              disabled={isUploading}
              data-testid="create-project-chat-upload-files-button"
            >
              {labels.upload}
            </ActionButton>
            <ActionButton
              filled={!hasItems}
              icon={<Globe className="size-3" />}
              onClick={onAddWebLink}
              data-testid="create-project-chat-add-web-link-button"
            >
              {labels.webLink}
            </ActionButton>
            {hasItems ? (
              <ActionButton
                filled
                onClick={onSaveFiles}
                disabled={isUploading}
                data-testid="create-project-chat-save-files-button"
              >
                {labels.save}
              </ActionButton>
            ) : (
              <ActionButton filled={false} onClick={onSkipFiles} data-testid="create-project-chat-skip-files-button">
                {labels.skip}
              </ActionButton>
            )}
          </div>
          {isUploading && (
            <div className="flex items-center gap-2 text-[12px] text-innoveraZinc-500">
              <Spinner aria-label={t("loading")} className="size-3" />
              <span>Uploading...</span>
            </div>
          )}
        </div>
      );
    }

    if (step === "dimensions") {
      const dim = dimensions[pendingDimensionIndex];
      if (!dim) return null;
      // Text dimensions are answered via the composer, but still offer a Skip button
      // like the choice ones do.
      if (dim.type !== "dropdown") {
        return (
          <div className="ml-[58px] flex items-center gap-2 pb-3 pr-6">
            <ActionButton
              filled={false}
              onClick={() => onDimensionSkip?.()}
              data-testid="create-project-chat-dimension-skip-button"
            >
              {labels?.dimensionSkip ?? "Skip"}
            </ActionButton>
          </div>
        );
      }
      const options = dim.options ?? [];
      return (
        <div className="ml-[58px] flex flex-col gap-2 pb-3 pr-6">
          <div className="flex flex-col gap-1.5">
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => onDimensionOptionChange?.(opt)}
                data-testid={`create-project-chat-dimension-option-${opt}-button`}
                className="flex items-center gap-2 text-left"
              >
                <span
                  className={cn(
                    "flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors",
                    selectedDimensionOption === opt
                      ? "border-innoveraZinc-900 bg-innoveraZinc-900"
                      : "border-innoveraZinc-300 bg-white"
                  )}
                >
                  {selectedDimensionOption === opt && (
                    <span className="size-2 rounded-full bg-white" />
                  )}
                </span>
                <FigmaTypography component="span" variant="body-2" color="zinc900">{opt}</FigmaTypography>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <ActionButton
              filled
              onClick={() => onDimensionConfirm?.(selectedDimensionOption)}
              disabled={!selectedDimensionOption}
              data-testid="create-project-chat-dimension-confirm-button"
            >
              {labels?.dimensionConfirm ?? "Confirm"}
            </ActionButton>
            <ActionButton
              filled={false}
              onClick={() => onDimensionSkip?.()}
              data-testid="create-project-chat-dimension-skip-button"
            >
              {labels?.dimensionSkip ?? "Skip"}
            </ActionButton>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div className="relative flex-1 min-h-0">
        {showScrollDown && (
          <button
            onClick={() => {
              scrollToBottom();
              setShowScrollDown(false);
            }}
            data-testid="create-project-chat-message-list-scroll-down-button"
            className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-white border border-gray-200 shadow-md rounded-full p-2 hover:bg-gray-50 transition-colors"
          >
            <ChevronDown className="w-5 h-5 text-gray-600" />
          </button>
        )}
        <div ref={scrollContainerRef} onScroll={checkIfScrollable} className="h-full overflow-y-auto py-4">
          <div ref={contentRef}>
            {messages.map((message, index) => (
              <div key={message.id}>
                <CaseBriefChatMessage
                  message={message}
                  isLoading={message.id === loadingMessageId || (isLoading && !loadingMessageId && index === messages.length - 1 && message.role === "assistant")}
                  progressMessage={
                    isLoading && index === messages.length - 1 && message.role === "assistant"
                      ? statusMessage
                      : undefined
                  }
                />
                {message.role === "assistant" && renderStepButtons(index)}
              </div>
            ))}

            {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
              <CaseBriefChatMessage
                message={{ id: "loading", role: "assistant", content: "" }}
                isLoading
                progressMessage={statusMessage}
              />
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}
